import { useState } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { Download, Loader2, CloudUpload, AlertCircle, Trash2, Pencil, Check, X, LockOpen } from 'lucide-preact'
import type { WebAssetViewDto, AssetVersionViewDto, ApprovalCommentViewDto, EmailCopyActivationViewDto } from '@/api/asset'
import type { AssetQaRunViewDto } from '@/types/assetQa'
import type { AssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { BRAND_CLASSES } from '@/constants/theme'
import { ASSET_TYPE_META, DEFAULT_ASSET_META, ASSET_STATUS_COLORS } from '../constants/assetMeta'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { AssetCommentThread } from './AssetCommentThread'
import { AssetQaPanel } from './AssetQaPanel'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader'
import { QA_VERDICT_META } from '../constants/assetQaMeta'
import { countOpenFindings } from './assetQaCounts'
import { UploadVersionModal } from '../modals/UploadVersionModal'
import { SignOffModal } from '../modals/SignOffModal'
import { EmailCopyActivationList } from '../modals/EmailCopyActivationList'
import { shortAgo } from '@/utils/time'
import { TextBoxField } from '@/components'
import { formatBytes } from '@/utils/fileSize'

interface StatusAction {
  label: string
  targetStatus: string
  signOff?: boolean
  variant: 'primary' | 'secondary'
}

const STATUS_ACTIONS: Record<string, StatusAction[]> = {
  'Ready for Sign-Off': [
    { label: 'Feedback Submitted', targetStatus: 'Feedback Submitted', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
  'Reopened': [
    { label: 'Ready for Sign-Off', targetStatus: 'Ready for Sign-Off', variant: 'secondary' },
    { label: 'Feedback Submitted', targetStatus: 'Feedback Submitted', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
  'Feedback Submitted': [
    { label: 'Ready for Sign-Off', targetStatus: 'Ready for Sign-Off', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
}

const NAME_MAX_LENGTH = 255

interface AssetReviewSidePanelProps {
  assetDetail: WebAssetViewDto | null
  versions: AssetVersionViewDto[]
  comments: ApprovalCommentViewDto[]
  selectedVersionNumber: number
  onVersionChange: (versionNumber: number) => void
  onSignOff: (comment?: string) => Promise<void>
  onAddComment: (text: string, parentCommentId?: string) => Promise<void>
  onUploadVersion: (file: File, versionComment?: string) => Promise<void>
  onUploadVersionByCloudStorage: (fileId: string, fileName: string, mimeType: string, provider: string, versionComment?: string) => Promise<void>
  onStatusChange: (newStatus: string) => Promise<void>
  onNameChange: (newName: string) => Promise<void>
  onDeleteAsset: () => Promise<void>
  loading: boolean
  permissions: AssetReviewPermissions
  /** The QA result for the selected version. Null once loaded means it was never checked. */
  qaRun: AssetQaRunViewDto | null
  qaLoading: boolean
  /** Set when the read failed — distinct from a version that was never checked. */
  qaLoadError: string | null
  /** False for asset types the checkpoint does not cover, which hides the section entirely. */
  qaApplies: boolean
  onOverrideQaFinding: (checkId: string, code: string, reason: string) => Promise<void>
  /** Sends the reader to the web tab that owns a QA gap's field, closing the overlay (ORB-889). */
  onGoToWebTab?: (tab: WebTabKey) => void
  /** The email activations that send this copy (ORB-303). Signing off moves them to the version signed off, and
   *  reopening stops them, so both say which ones first. Empty for every other asset type. */
  copyActivations?: EmailCopyActivationViewDto[]
}

function getVersionOptionLabel(v: AssetVersionViewDto): string {
  if (!v.UploadStatus || v.UploadStatus === 'Completed') {
    return `v${v.VersionNumber} — ${v.OriginalFileName || 'Unknown'} (${formatBytes(v.FileSizeBytes)})`
  }
  if (v.UploadStatus === 'Pending') return `v${v.VersionNumber} — Queued for download...`
  if (v.UploadStatus === 'Downloading') return `v${v.VersionNumber} — Downloading...`
  if (v.UploadStatus === 'Failed') return `v${v.VersionNumber} — Upload failed`
  return `v${v.VersionNumber} — ${v.UploadStatus}...`
}

export function AssetReviewSidePanel({
  assetDetail,
  versions,
  comments,
  selectedVersionNumber,
  onVersionChange,
  onSignOff,
  onAddComment,
  onUploadVersion,
  onUploadVersionByCloudStorage,
  onStatusChange,
  onNameChange,
  onDeleteAsset,
  loading,
  permissions,
  qaRun,
  qaLoading,
  qaLoadError,
  qaApplies,
  onOverrideQaFinding,
  onGoToWebTab,
  copyActivations = [],
}: AssetReviewSidePanelProps) {
  /**
   * Which sections are collapsed (ORB-889).
   *
   * ⚑ Deliberately not persisted. The overlay is transient — opened on an asset, closed, opened on
   * the next — and a reviewer working a batch would otherwise inherit a state from three assets ago
   * without remembering they set it.
   *
   * ⚑⚑ Quality check starts COLLAPSED (ORB-987), reversing the ORB-889 decision that findings should
   * be the first thing seen. They were, and on a gradient asset that was 43 of them: the panel read
   * as the platform rejecting the work rather than as an assistant offering an opinion. The header's
   * open-finding count is what carries the signal now, so nothing is hidden from a reader scanning
   * for whether there is anything to do — it is one click instead of a wall.
   */
  const [qaCollapsed, setQaCollapsed] = useState(true)
  const [commentsCollapsed, setCommentsCollapsed] = useState(false)

  const [showSignOffModal, setShowSignOffModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [updatingTargetStatus, setUpdatingTargetStatus] = useState<string | null>(null)

  // Inline name editing
  const [isEditingName, setIsEditingName] = useState(false)
  const [isSavingName, setIsSavingName] = useState(false)

  const {
    control,
    handleSubmit: handleNameSubmit,
    formState: { isValid: isNameValid },
    reset: resetNameForm,
  } = useForm<{ Name: string }>({
    mode: 'onChange',
    defaultValues: { Name: '' },
  })

  if (!assetDetail) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  const typeMeta = ASSET_TYPE_META[assetDetail.Type] || DEFAULT_ASSET_META
  const TypeIcon = typeMeta.Icon
  const statusColors = ASSET_STATUS_COLORS[assetDetail.Status] || BRAND_CLASSES.STATUS.DEFAULT

  // Unknown verdicts render nothing rather than a bare string: Verdict is `string` on the wire, so
  // this lookup is an assertion and not a check, and a value we do not recognise is one we cannot
  // colour honestly.
  // ⚑⚑ `qaApplies` as well as the run (ORB-982). This pill sits in the badges row rather than inside
  // the Quality check section, so it was the one QA surface the gate did not reach — and the overlay's
  // own comment claimed it did. A reader whose access is revoked kept the verdict beside the status
  // until the clearing effect ran. Raised by Copilot on PR 243.
  const qaVerdictMeta = qaApplies && qaRun ? QA_VERDICT_META[qaRun.Verdict] : undefined
  const isSignedOff = assetDetail.Status === 'Signed Off'
  const allActions = STATUS_ACTIONS[assetDetail.Status] || []
  // Signing off moves these to the version signed off (ORB-303); one already on it is not changed.
  const activationsSignOffMoves = copyActivations.filter(
    activation => activation.EmailAssetVersionNumber !== assetDetail.CurrentVersionNumber)

  const reopen = async () => {
    setUpdatingTargetStatus('Reopened')
    try {
      await onStatusChange('Reopened')
    } catch (error) {
      console.error('Failed to update asset status:', error)
    } finally {
      setUpdatingTargetStatus(null)
    }
  }

  // Filter status actions based on permissions
  const currentActions = allActions.filter(action => {
    if (action.signOff) return permissions.canSignOff
    return permissions.canChangeStatus
  })

  const commentsDisabled = permissions.isCommentsDisabled(isSignedOff)

  const selectedVersion = versions.find(v => v.VersionNumber === selectedVersionNumber)
  const selectedUploadStatus = selectedVersion?.UploadStatus
  const isSelectedVersionProcessing = selectedUploadStatus === 'Pending' || selectedUploadStatus === 'Downloading'
  const isSelectedVersionFailed = selectedUploadStatus === 'Failed'

  // --- Name editing helpers ---

  const startEditingName = () => {
    if (isSignedOff || !permissions.canEditName) return
    resetNameForm({ Name: assetDetail.Name })
    setIsEditingName(true)
  }

  const cancelEditingName = () => {
    setIsEditingName(false)
    resetNameForm({ Name: '' })
  }

  const saveEditedName = handleNameSubmit(async (data) => {
    const trimmed = data.Name.trim()
    if (trimmed === assetDetail.Name) {
      cancelEditingName()
      return
    }
    setIsSavingName(true)
    try {
      await onNameChange(trimmed)
      setIsEditingName(false)
      resetNameForm({ Name: '' })
    } catch (error) {
      console.error('Failed to save asset name:', error)
    } finally {
      setIsSavingName(false)
    }
  })

  const handleNameKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEditedName()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditingName()
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header + Details. No bottom border: every divider below is drawn by the section beneath it,
          so one here would double up with the first section's top border (round 4). */}
      <div className="flex-shrink-0 p-4 space-y-3">
        {/* Title + actions */}
        <div className="flex items-start justify-between gap-2">
          {isEditingName ? (
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-1">
                <TextBoxField
                  control={control}
                  name="Name"
                  placeholder="Enter asset name"
                  autoFocus
                  disabled={isSavingName}
                  maxLength={NAME_MAX_LENGTH}
                  onKeyDown={handleNameKeyDown}
                  containerClassName="flex-1 min-w-0"
                  className="!py-1 !text-base !font-semibold"
                  rules={{
                    required: 'Asset name is required',
                    validate: value => {
                      const trimmed = (value ?? '').trim()
                      if (trimmed.length === 0) return 'Asset name is required'
                      if (trimmed.length > NAME_MAX_LENGTH) return `Asset name must be ${NAME_MAX_LENGTH} characters or less`
                      return true
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={saveEditedName}
                  disabled={isSavingName || !isNameValid}
                  className="inline-flex items-center justify-center w-7 h-7 mt-0.5 rounded-md text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Save"
                >
                  {isSavingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={cancelEditingName}
                  disabled={isSavingName}
                  className="inline-flex items-center justify-center w-7 h-7 mt-0.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="group flex items-start gap-1 min-w-0 flex-1">
              {/* Gated at render, matching the pencil below. This carried an unconditional handler
                  and defended itself inside startEditingName, so two affordances for one action
                  were guarded in two different places (ORB-666). */}
              <h2
                className={`text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight break-words min-w-0 transition-colors ${!isSignedOff && permissions.canEditName ? 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400' : 'cursor-default'}`}
                onClick={!isSignedOff && permissions.canEditName ? startEditingName : undefined}
                title={!permissions.canEditName ? undefined : isSignedOff ? 'Reopen to edit name' : 'Click to edit name'}
              >
                {assetDetail.Name}
              </h2>
              {!isSignedOff && permissions.canEditName && (
                <button
                  type="button"
                  onClick={startEditingName}
                  className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all mt-0.5"
                  title="Edit name"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="flex-shrink-0 flex items-center gap-0.5">
            {assetDetail.CurrentVersionBlobUrl && (
              <a
                href={assetDetail.CurrentVersionBlobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
            )}
            {permissions.canDeleteAsset && (
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                disabled={isSignedOff}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={isSignedOff ? 'Reopen to delete asset' : 'Delete asset'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeMeta.badgeBg} ${typeMeta.color}`}>
            <TypeIcon className="w-3 h-3" />
            {assetDetail.Type}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors}`}>
            {assetDetail.Status}
          </span>
          {/* ⚑ Immediately after the status, because the two are claims about the same asset and were
              previously drawn in opposite corners of the screen — the verdict beside the overlay
              title, the status here (ORB-889). A reader had to find both halves and reconcile them.
              The same adjacency makes the web header legible, where the readiness chip sits right
              before the Sign Off control.

              This row already wraps, which is what makes it safe to add a fifth thing: the longest
              verdict ("Verified where possible") drops to its own line rather than clipping, and a
              truncated verdict is worse than the split it replaces — "Do not submit…" is not a
              verdict anyone can rely on. */}
          {qaVerdictMeta && (
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${qaVerdictMeta.classes}`}
              title="Quality check result for the version on screen"
            >
              {qaVerdictMeta.label}
            </span>
          )}
          {isSignedOff && permissions.canReopen && (
            <button
              onClick={async () => {
                // Reopening stops the activations on this email (ORB-303), so it says which first.
                if (copyActivations.length > 0) {
                  setShowReopenModal(true)
                  return
                }
                await reopen()
              }}
              disabled={updatingTargetStatus !== null}
              className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reopen"
            >
              {updatingTargetStatus === 'Reopened' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LockOpen className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Status Actions */}
        {!isSignedOff && currentActions.length > 0 && (
          <div className="flex gap-2">
            {currentActions.map((action) => (
              <button
                key={action.targetStatus}
                onClick={async () => {
                  if (action.signOff) {
                    // An email that activations send always confirms, so the reviewer sees which sends move (ORB-303).
                    if (!permissions.requireSignOffConfirmation && activationsSignOffMoves.length === 0) {
                      setUpdatingTargetStatus(action.targetStatus)
                      try {
                        await onSignOff()
                      } finally {
                        setUpdatingTargetStatus(null)
                      }
                    } else {
                      setShowSignOffModal(true)
                    }
                    return
                  }
                  setUpdatingTargetStatus(action.targetStatus)
                  try {
                    await onStatusChange(action.targetStatus)
                  } catch (error) {
                    console.error('Failed to update asset status:', error)
                  } finally {
                    setUpdatingTargetStatus(null)
                  }
                }}
                disabled={updatingTargetStatus !== null}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  action.variant === 'primary'
                    ? BRAND_CLASSES.BUTTON.PRIMARY
                    : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {updatingTargetStatus === action.targetStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Version Selector */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
            Version
          </label>
          <div className="flex gap-1.5">
            <select
              value={selectedVersionNumber}
              onChange={(e) => onVersionChange(Number((e.target as HTMLSelectElement).value))}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
            {versions
              .slice()
              .sort((a, b) => b.VersionNumber - a.VersionNumber)
              .map((v) => (
                <option key={v.Id} value={v.VersionNumber}>
                  {getVersionOptionLabel(v)}
                </option>
              ))}
            </select>
            {/* An email copy's versions come from Save as Version in its editor, never a file (ORB-303). */}
            {permissions.canUploadVersion && assetDetail.Type !== 'Email Copy' && (
              <button
                onClick={() => setShowUploadModal(true)}
                disabled={isSignedOff}
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
                title={isSignedOff ? 'Reopen to upload a new version' : 'Upload new version'}
              >
                <CloudUpload className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Version upload status indicator */}
          {isSelectedVersionProcessing && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Loader2 className="w-3 h-3 text-primary-500 animate-spin flex-shrink-0" />
              <p className="text-[11px] text-primary-600 dark:text-primary-400">
                Processing upload...
              </p>
            </div>
          )}

          {isSelectedVersionFailed && (
            <div className="flex items-start gap-1.5 mt-1.5">
              <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-600 dark:text-red-400">
                {selectedVersion?.UploadErrorMessage || 'Upload failed'}
              </p>
            </div>
          )}

          {selectedVersion && !isSelectedVersionProcessing && !isSelectedVersionFailed && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
              Uploaded {shortAgo(selectedVersion.UploadedAt)} by {selectedVersion.UploadedBy}
            </p>
          )}
        </div>
      </div>

      {/* Quality check. Above the comment thread on purpose: it is what a reviewer should read
          before forming an opinion, and the thread grows without bound beneath it. */}
      {qaApplies && (
        <div
          className={
            // ⚑⚑ The collapse is about the SPACE, not the toggle (ORB-889). These two sections divide
            // the panel's height under different rules — this one capped at 45% and non-shrinking,
            // the thread below taking what is left. Collapsed, it must stop reserving anything or it
            // leaves a dead gap under its own header; expanded with the thread collapsed, it must be
            // free to grow past the 45% cap or the reclaimed space goes nowhere.
            //
            // ⚑⚑ `flex flex-col` with the scroll moved to the CHILD, not here. The header is a child
            // of this element, so an `overflow-y-auto` at this level scrolled the title away along
            // with the findings under it — the section behaved like a page, not an accordion. The
            // header now stays put and only the content moves (round 4).
            'flex flex-col border-t border-slate-200 dark:border-slate-700 ' +
            (qaCollapsed
              ? 'flex-shrink-0'
              : commentsCollapsed
                ? 'flex-1 min-h-0'
                : 'max-h-[45%] flex-shrink-0')
          }
        >
          <CollapsibleSectionHeader
            title="Quality check"
            // ⚑ Open findings only, and the header draws no number at zero rather than a "(0)" —
            // which would be the one thing a collapsed section must not do, since "(0)" beside a
            // collapsed title asserts a clean pass on checks that may never have run. The coverage
            // gap is the verdict pill's job, in the status row above (ORB-987).
            count={countOpenFindings(qaRun)}
            collapsed={qaCollapsed}
            onToggle={() => setQaCollapsed(value => !value)}
          />

          {/* ⚑⚑ Collapsing must not hide a state whose ONLY signal is inside the panel (ORB-987).
              A run that is loading, failed to load, or was never taken all leave qaRun null — so the
              header draws no count and there is no verdict pill either, and the section collapses over
              the one sentence that would have explained it. The reader is left with a silent accordion
              and no way to know there is anything behind it. A finding count can be absent because
              there is nothing to report; THIS cannot. Raised by Copilot on PR 244. */}
          {qaCollapsed && qaRun === null && (
            <p className="flex-shrink-0 px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400">
              {qaLoading
                ? 'Checking…'
                : qaLoadError
                  ? 'The quality check could not be loaded.'
                  : 'This version has not been checked.'}
            </p>
          )}

          {!qaCollapsed && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
              <AssetQaPanel
                run={qaRun}
                loading={qaLoading}
                notChecked={!qaLoading && !qaLoadError && qaRun === null}
                loadError={qaLoadError}
                canOverride={permissions.canOverrideQaFinding}
                onOverride={onOverrideQaFinding}
                onGoToWebTab={onGoToWebTab}
              />
            </div>
          )}
        </div>
      )}

      {/* Comments. Same accordion shape as the section above: its own top border, a header that
          stays put, and a body that scrolls — the thread already scrolls internally, so only the
          divider was missing. Without it the header ran straight on from the findings above with no
          line between them, which is what made the panel read as one long column rather than two
          sections (round 4). */}
      <div
        className={
          'flex flex-col border-t border-slate-200 dark:border-slate-700 ' +
          (commentsCollapsed ? 'flex-shrink-0' : 'flex-1 overflow-hidden min-h-0')
        }
      >
        <AssetCommentThread
          comments={comments}
          onAddComment={onAddComment}
          loading={loading}
          disabled={commentsDisabled}
          collapsed={commentsCollapsed}
          onToggleCollapsed={() => setCommentsCollapsed(value => !value)}
        />
      </div>

      {/* Sign Off Modal */}
      <SignOffModal
        isOpen={showSignOffModal}
        onClose={() => setShowSignOffModal(false)}
        assetDetail={assetDetail}
        onSignOff={onSignOff}
        activations={activationsSignOffMoves}
      />

      {/* Reopen Confirmation — only for an email activations send (ORB-303) */}
      <ConfirmationModal
        isOpen={showReopenModal}
        title="Reopen this email?"
        message={`${copyActivations.length === 1 ? 'This activation stops' : 'These activations stop'} sending until a version is signed off again:`}
        type="warning"
        confirmText="Reopen"
        loading={updatingTargetStatus === 'Reopened'}
        onCancel={() => setShowReopenModal(false)}
        onConfirm={async () => {
          await reopen()
          setShowReopenModal(false)
        }}
      >
        <EmailCopyActivationList activations={copyActivations} />
      </ConfirmationModal>

      {/* Upload Version Modal */}
      <UploadVersionModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        assetDetail={assetDetail}
        onUploadVersion={onUploadVersion}
        onUploadVersionByCloudStorage={onUploadVersionByCloudStorage}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Asset"
        message={`Are you sure you want to delete '${assetDetail.Name}'? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        loading={isDeleting}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          setIsDeleting(true)
          try {
            await onDeleteAsset()
          } finally {
            setIsDeleting(false)
            setShowDeleteModal(false)
          }
        }}
      />
    </div>
  )
}
